/**
 * Reporting Dashboard Component
 * Advanced analytics and reporting interface.
 * 
 * Features:
 * - Date range based filtering
 * - Multi-criteria filtering (status, product, assignee, client code)
 * - ODU Quick Reports (Daily, Monthly)
 * - CSV Export functionality
 * - Real-time query performance metrics
 * 
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronLeft, Search, AlertCircle, FileText, Clock, CheckCircle,
  TrendingUp, Users, Calendar, CalendarDays, Download, Filter, RotateCcw,
  ChevronRight, RefreshCw, X, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { AdvancedMultiSelect } from '../filters/FilterComponents';
import { 
  formatDateTimeShortET, 
  formatDateShortET, 
  formatUtcToET as centralFormatUtcToET,
  getTimestampTooltip as centralGetTimestampTooltip 
} from '../../utils/formatters';
// Extracted components
import { 
  ReportCriteriaCard, 
  ReportResultsTable, 
  ReportLoadingState, 
  ReportErrorState 
} from './components';
import { normalizeBorrowerName } from '../../utils/nameNormalizer';
import { useWorkspacePresentation } from '../../hooks/useWorkspacePresentation';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Reporting Dashboard Component
const ReportingDashboard = ({ user, setActiveView }) => {
  // OD-PARITY-001: Order Desk report panel for native role OR Order Desk workspace
  const { isOrderDeskView } = useWorkspacePresentation(user);
  const [analytics, setAnalytics] = useState(null);
  const [exportPerformance, setExportPerformance] = useState(null);
  const [showRowCapWarning, setShowRowCapWarning] = useState(false);
  const [reportResults, setReportResults] = useState(null);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [queryTime, setQueryTime] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Status badge variant helper function
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'created': return 'secondary';
      case 'open': return 'default';
      case 'pending': return 'outline';
      case 'clarification': return 'destructive';
      case 'mailaway': return 'outline';
      case 'mail_away': return 'outline';
      case 'mail_away_received': return 'default';  // Distinct style for received
      case 'complete': return 'default';
      case 'processing': return 'default';
      case 'completed': return 'default';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };
  
  // Format property address helper function
  const formatPropertyAddress = (order) => {
    if (!order) return '—';
    
    // Try multiple possible field names for property address
    const address = order.property_address || 
                    order.propertyAddress || 
                    order.address ||
                    order.property_street;
    
    if (address) return address;
    
    // Build address from components if available
    const parts = [];
    if (order.property_street || order.street) parts.push(order.property_street || order.street);
    if (order.property_city || order.city) parts.push(order.property_city || order.city);
    if (order.property_state || order.state) parts.push(order.property_state || order.state);
    if (order.property_zip || order.zip) parts.push(order.property_zip || order.zip);
    
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  // V1 REQUIREMENT: Use centralized ET formatters
  const getTimestampTooltip = centralGetTimestampTooltip;

  // REPORT-SCOPE-001 — role-based default scope: Tax TL → tax,
  // Title Search TL → search. Multi-scope roles (Admin/Order Desk)
  // get NO default — they must actively pick a scope (placeholder).
  function getDefaultModuleScope() {
    try {
      const role = JSON.parse(localStorage.getItem('user') || '{}').role || '';
      if (role === 'tax_search_team_leader' || role === 'team_leader') return 'tax';
      if (role === 'title_search_team_leader') return 'search';
    } catch { /* noop */ }
    return '';
  }

  // Format UTC date to Eastern Time - uses centralized formatter
  const formatUtcToET = (dateStr, includeTime = true) => {
    if (!dateStr) return '—';
    return includeTime ? formatDateTimeShortET(dateStr) : formatDateShortET(dateStr);
  };

  const [filters, setFilters] = useState({
    dateRangePreset: 'last_30_days', // Default to Last 30 Days for better performance
    customStartDate: null, // Start empty (null) as per specification  
    customEndDate: null, // Start empty (null) as per specification
    reportType: 'all', // New: all, open, closed
    dateType: 'creation', // New: creation, closure
    // REPORT-SCOPE-001 — role-based default: tax TL → tax, title TL → search
    moduleScope: getDefaultModuleScope(),
    statuses: [],
    clientProducts: [],
    assignedUsers: [],
    clientCodes: [],
    includeAllStatuses: true,
    includeAllClientProducts: true,
    includeAllAssignedUsers: true,
    includeAllClientCodes: true,
    // Feb 2026 — Option C parity toggle. Default ON: $or in active
    // prior-period orders alongside the in-range result set (matches
    // the historical dashboard ↔ report behaviour). When cleared by
    // the operator, strict date-range filtering on the selected
    // column is applied across all four export paths.
    includeCarryForward: true,
  });
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    clientProducts: [],
    assignedUsers: [],
    clientCodes: []
  });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchFilterOptions();
    loadSavedFilters();
    fetchAnalytics();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      // Use v2 endpoint for filter values
      const response = await axios.get(`${API}/reports-v2/filters/values`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Fallback to v1 if v2 fails
      try {
        const fallbackResponse = await axios.get(`${API}/reports/filters/values`);
        setFilterOptions(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Fallback filter fetch also failed:', fallbackError);
      }
    }
  };

  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem(`titleflow-filters-${user?.id}`);
      if (saved) {
        const parsedFilters = JSON.parse(saved);
        // ✅ FIX: For Reports module, ensure dates start empty as per specification
        const safeFilters = { 
          ...parsedFilters,
          customStartDate: null, // Always start empty
          customEndDate: null    // Always start empty
        };
        // REPORT-SCOPE-001 — scope is never restored from saved filters:
        // TL roles get their locked default, multi-scope roles must
        // actively re-select (placeholder) each session.
        safeFilters.moduleScope = getDefaultModuleScope();
        setFilters(prev => ({ ...prev, ...safeFilters }));
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  };

  const saveFilters = (newFilters) => {
    try {
      localStorage.setItem(`titleflow-filters-${user?.id}`, JSON.stringify(newFilters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Date range
      params.append('date_range_preset', filters.dateRangePreset);
      if (filters.dateRangePreset === 'custom') {
        if (filters.customStartDate) params.append('start_date', new Date(filters.customStartDate).toISOString());
        if (filters.customEndDate) params.append('end_date', new Date(filters.customEndDate).toISOString());
      }
      
      // Multi-select filters
      params.append('include_all_statuses', filters.includeAllStatuses.toString());
      params.append('include_all_client_products', filters.includeAllClientProducts.toString());
      params.append('include_all_assigned_users', filters.includeAllAssignedUsers.toString());
      params.append('include_all_client_codes', filters.includeAllClientCodes.toString());
      // REPORT-SCOPE-001 — Module/Report Scope
      params.append('module_scope', filters.moduleScope || 'all');
      
      if (!filters.includeAllStatuses) {
        filters.statuses.forEach(status => params.append('statuses', status));
      }
      if (!filters.includeAllClientProducts) {
        filters.clientProducts.forEach(product => params.append('client_products', product));
      }
      if (!filters.includeAllAssignedUsers) {
        filters.assignedUsers.forEach(user => params.append('assigned_to_users', user));
      }
      if (!filters.includeAllClientCodes) {
        filters.clientCodes.forEach(code => params.append('client_codes', code));
      }
      
      // Use the fixed advanced analytics endpoint to get export performance data
      console.log('Fetching analytics with params:', params.toString());
      const response = await axios.get(`${API}/reports/analytics/advanced?${params}`);
      console.log('Analytics response:', response.data);
      
      // Handle the advanced analytics response structure
      if (response.data.analytics) {
        setAnalytics(response.data.analytics);
      }
      if (response.data.export_performance) {
        setExportPerformance(response.data.export_performance);
        setShowRowCapWarning(response.data.export_performance.row_cap_warning?.show_warning || false);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Don't set analytics error here to avoid duplicate error handling
      // The runReport function handles errors separately
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    saveFilters(updatedFilters);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'dateRange':
        updateFilters({ 
          dateRangePreset: value,
          customStartDate: value === 'custom' ? filters.customStartDate : '',
          customEndDate: value === 'custom' ? filters.customEndDate : ''
        });
        break;
      case 'statuses':
        updateFilters({ 
          statuses: value,
          includeAllStatuses: value.length === 0
        });
        break;
      case 'clientProducts':
        updateFilters({ 
          clientProducts: value,
          includeAllClientProducts: value.length === 0
        });
        break;
      case 'assignedUsers':
        updateFilters({ 
          assignedUsers: value,
          includeAllAssignedUsers: value.length === 0
        });
        break;
      case 'clientCodes':
        updateFilters({ 
          clientCodes: value,
          includeAllClientCodes: value.length === 0
        });
        break;
    }
  };

  const handleCustomDateChange = (startDate, endDate) => {
    updateFilters({
      customStartDate: startDate,
      customEndDate: endDate
    });
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRangePreset: 'last_30_days', // Default to Last 30 Days for better performance
      customStartDate: null, // ✅ SPEC: Start empty (null)
      customEndDate: null, // ✅ SPEC: Start empty (null)  
      reportType: 'all', // Reset report type
      dateType: 'creation', // Reset date type
      moduleScope: getDefaultModuleScope(), // REPORT-SCOPE-001 — back to role default
      statuses: [],
      clientProducts: [],
      assignedUsers: [],
      clientCodes: [],
      includeAllStatuses: true,
      includeAllClientProducts: true,
      includeAllAssignedUsers: true,
      includeAllClientCodes: true
    };
    setFilters(defaultFilters);
    saveFilters(defaultFilters);
    // Clear results when resetting filters
    setReportResults(null);
    setReportError(null);
  };

  const runReport = async () => {
    // REPORT-SCOPE-001 — a scope must be actively selected before generating.
    if (!filters.moduleScope) {
      setReportError('Please select a Report Scope before generating the report.');
      return;
    }
    setIsRunningReport(true);
    setReportError(null);
    const startTime = Date.now();
    
    try {
      const params = new URLSearchParams();
      
      // Date range handling
      const today = new Date();
      let startDate, endDate;
      
      switch (filters.dateRangePreset) {
        case 'last_7_days':
          startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          endDate = today;
          break;
        case 'last_30_days':
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = today;
          break;
        case 'this_month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = today;
          break;
        case 'last_month':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case 'custom':
          if (!filters.customStartDate || !filters.customEndDate) {
            setReportError('Please select both start and end dates for custom range.');
            setIsRunningReport(false);
            return;
          }
          startDate = new Date(filters.customStartDate);
          endDate = new Date(filters.customEndDate);
          
          // Validate date range (max 365 days)
          const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
          if (daysDiff > 365) {
            setReportError('Date range cannot exceed 365 days. Please select a shorter range.');
            setIsRunningReport(false);
            return;
          }
          if (startDate > endDate) {
            setReportError('Start date cannot be after end date.');
            setIsRunningReport(false);
            return;
          }
          break;
        default:
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = today;
      }
      
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', endDate.toISOString().split('T')[0]);
      
      // Add other filters
      if (!filters.includeAllStatuses && filters.statuses.length > 0) {
        filters.statuses.forEach(status => params.append('status', status));
      }
      if (!filters.includeAllClientProducts && filters.clientProducts.length > 0) {
        filters.clientProducts.forEach(product => params.append('client_product', product));
      }
      if (!filters.includeAllAssignedUsers && filters.assignedUsers.length > 0) {
        filters.assignedUsers.forEach(user => params.append('assigned_user', user));
      }
      if (!filters.includeAllClientCodes && filters.clientCodes.length > 0) {
        filters.clientCodes.forEach(code => params.append('client_code', code));
      }
      
      // Add pagination and sorting
      params.append('page', currentPage.toString());
      params.append('limit', '50'); // Use 'limit' parameter which should work with orders endpoint
      
      // Use the working orders endpoint instead of the failing advanced analytics endpoint
      console.log('Fetching reports with params:', params.toString());
      const response = await axios.get(`${API}/orders?${params}`);
      const endTime = Date.now();
      
      console.log('Report response:', response.data);
      
      // Handle different possible response formats
      let orders = [];
      let totalCount = 0;
      
      if (Array.isArray(response.data)) {
        // Simple array response
        orders = response.data;
        totalCount = response.data.length;
      } else if (response.data.orders) {
        // Paginated response with orders array
        orders = response.data.orders;
        totalCount = response.data.total || response.data.total_count || response.data.orders.length;
      } else {
        // Unexpected format
        console.warn('Unexpected response format:', response.data);
        orders = [];
        totalCount = 0;
      }
      
      // Format the response for our results table
      const formattedResults = {
        orders: orders,
        total_count: totalCount,
        page: currentPage,
        page_size: 50
      };
      
      setReportResults(formattedResults);
      setQueryTime(endTime - startTime);
      
      // Show success message if we got results
      if (orders.length > 0) {
        console.log(`✅ Successfully loaded ${orders.length} orders in ${endTime - startTime}ms`);
      } else {
        console.log('⚠️ Query returned no results - this may be expected based on filters');
      }
      
    } catch (error) {
      console.error('Error running report:', error);
      let errorMessage = 'Something went wrong while generating the report.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        console.error('API Error:', status, error.response.data);
        
        if (status === 400) {
          errorMessage = 'Invalid filter parameters. Please check your selections and try again.';
        } else if (status === 401) {
          errorMessage = 'Authentication required. Please log in and try again.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to access reports.';
        } else if (status === 404) {
          errorMessage = 'Reports endpoint not found. Please contact support.';
        } else if (status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (status >= 500) {
          errorMessage = 'System error: Please retry or adjust filters. If the issue persists, contact support.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = 'Unexpected error occurred. Please retry or contact support.';
      }
      
      setReportError(errorMessage);
    } finally {
      setIsRunningReport(false);
    }
  };

  const removeFilter = (filterType, value) => {
    switch (filterType) {
      case 'dateRange':
        updateFilters({ dateRangePreset: 'last_30_days' }); // Default to Last 30 Days
        break;
      case 'status':
        const newStatuses = filters.statuses.filter(s => s !== value);
        updateFilters({ 
          statuses: newStatuses,
          includeAllStatuses: newStatuses.length === 0
        });
        break;
      case 'clientProduct':
        const newProducts = filters.clientProducts.filter(p => p !== value);
        updateFilters({ 
          clientProducts: newProducts,
          includeAllClientProducts: newProducts.length === 0
        });
        break;
      case 'assignedUser':
        const newUsers = filters.assignedUsers.filter(u => u !== value);
        updateFilters({ 
          assignedUsers: newUsers,
          includeAllAssignedUsers: newUsers.length === 0
        });
        break;
      case 'clientCode':
        const newCodes = filters.clientCodes.filter(c => c !== value);
        updateFilters({ 
          clientCodes: newCodes,
          includeAllClientCodes: newCodes.length === 0
        });
        break;
    }
  };

  // Consolidated Client Status Report Export (Multi-sheet Excel)
  //
  // Production note (Feb 2026): at monthly volume (~45k orders) the Excel
  // build on the backend takes ~50s. We set an explicit 3-minute axios
  // timeout (well above the measured worst case + safety margin for
  // 60-80k order spikes) and show a long-running toast on click so the
  // user knows the wait is expected. The toast.loading id is captured so
  // success/error paths can dismiss it cleanly.
  const handleConsolidatedReportExport = async () => {
    setLoading(true);
    const exportToastId = toast.loading(
      'Generating export… this may take up to 2 minutes',
      { duration: Infinity }
    );
    try {
      const exportData = {
        start_date: filters.customStartDate ? 
          new Date(filters.customStartDate).toISOString() : null,
        end_date: filters.customEndDate ? 
          new Date(filters.customEndDate).toISOString() : null,
        // Jul 2026 fix — the selected Date Type (creation/closure) was
        // never sent, so the Excel silently used 'creation' + carry-forward
        // and closure reports contained out-of-range Completed At rows.
        date_type: filters.dateType || 'creation',
        // Jul 2026 fix — Report Type was also never sent, so a
        // "Completed" Excel contained every status.
        report_type: filters.reportType || 'all',
        statuses: filters.includeAllStatuses ? null : filters.statuses,
        client_codes: filters.includeAllClientCodes ? null : filters.clientCodes,
        client_products: filters.includeAllClientProducts ? null : filters.clientProducts,
        states: null, // Can be added if state filter is implemented
        // Feb 2026 — Option C parity toggle. Default ON.
        include_carry_forward: filters.includeCarryForward !== false,
        // REPORT-SCOPE-001 — Module/Report Scope
        module_scope: filters.moduleScope || 'all',
      };

      const response = await axios.post(`${API}/reports-v2/consolidated-status/export`, exportData, {
        responseType: 'blob',
        timeout: 180000, // 3 min — covers 45k production volume (~50s) with headroom for 60-80k spikes
      });

      // Create download link — response.data is already a typed Blob (responseType: 'blob')
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.setAttribute('download', `TitleFlow_Consolidated_Status_Report_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss(exportToastId);
      toast.success('Consolidated Client Status Report exported successfully');
    } catch (error) {
      console.error('Consolidated report export error:', error);
      toast.dismiss(exportToastId);
      // Distinguish a timeout from a server error so the user has actionable info.
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        toast.error(
          'Export timed out after 2 minutes. Try narrowing the date range or contact support if this persists.',
          { duration: 8000 }
        );
      } else {
        toast.error('Error exporting Consolidated Status Report');
      }
    } finally {
      setLoading(false);
    }
  };

  // ODU Quick Report functions
  const runODUReport = async (reportType) => {
    setIsRunningReport(true);
    setReportError(null);
    setReportResults(null);
    
    try {
      const startTime = Date.now();
      
      console.log(`Running ODU ${reportType} report...`);
      const response = await axios.get(`${API}/reports/odu/quick?report_type=${reportType}`);
      const endTime = Date.now();
      
      console.log('ODU Report response:', response.data);
      
      // Format the response for the results display
      setReportResults({
        orders: response.data.orders || [],
        total_count: response.data.summary.total_orders || 0,
        odu_report: response.data
      });
      
      setQueryTime(endTime - startTime);
      
      toast.success("Report generated successfully", {
        description: `Found ${response.data.summary.total_orders || 0} orders in ${endTime - startTime}ms`
      });
      
    } catch (error) {
      console.error(`Error running ODU ${reportType} report:`, error);
      setReportError(`Failed to generate ${reportType} report. Please try again.`);
      toast.error("Failed to generate report", {
        description: "Please try again or contact support if the problem persists."
      });
    } finally {
      setIsRunningReport(false);
    }
  };

  const runDailyReport = () => runODUReport('daily');
  const runMonthlyReport = () => runODUReport('monthly');


  const runCustomReport = async () => {
    console.log('🚀 runCustomReport function called!', { filters });
    setIsRunningReport(true);
    setReportError(null);
    setReportResults(null);
    
    try {
      const startTime = Date.now();
      
      // ✅ SPEC COMPLIANCE: UTC half-open bounds implementation
      // start = beginDate 00:00:00Z
      // end = (endDate + 1 day) 00:00:00Z  
      // Filter: created_at >= start AND created_at < end
      
      // Parse input dates (YYYY-MM-DD format)
      const startDateParts = filters.customStartDate.split('-');
      const endDateParts = filters.customEndDate.split('-');
      
      // Create UTC dates directly to avoid local timezone conversion issues
      const startYear = parseInt(startDateParts[0]);
      const startMonth = parseInt(startDateParts[1]) - 1; // Month is 0-indexed
      const startDay = parseInt(startDateParts[2]);
      
      const endYear = parseInt(endDateParts[0]);
      const endMonth = parseInt(endDateParts[1]) - 1; // Month is 0-indexed  
      const endDay = parseInt(endDateParts[2]);
      
      // Create UTC half-open interval as per specification
      const startDateUTC = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0)).toISOString();
      
      // ✅ FIX: Add 1 day to end date for half-open interval [start, end+1)
      const endDateUTC = new Date(Date.UTC(endYear, endMonth, endDay + 1, 0, 0, 0, 0)).toISOString();
      
      // ✅ INSTRUMENTATION: Log final query parameters and UTC window
      console.log('📊 REPORTS API CALL - Final Query Parameters:', {
        originalDates: {
          startInput: filters.customStartDate,
          endInput: filters.customEndDate
        },
        utcWindow: {
          start: startDateUTC,
          end: endDateUTC,
          description: `Half-open interval [${startDateUTC}, ${endDateUTC})`
        },
        reportType: filters.reportType || 'all',
        dateType: filters.dateType || 'creation',
        timezone_independent: true
      });
      
      const params = new URLSearchParams({
        start_date: startDateUTC,
        end_date: endDateUTC,
        report_type: filters.reportType || 'all',
        date_type: filters.dateType || 'creation',
        module_scope: filters.moduleScope || 'all',
        timezone_independent: 'true',
        // Feb 2026 — Option C parity toggle. Default ON.
        include_carry_forward: String(filters.includeCarryForward !== false),
      });
      
      // Log the exact URL being called
      const apiUrl = `${API}/reports/order-desk/custom?${params}`;
      console.log('🌐 API Request URL:', apiUrl);
      
      const response = await axios.get(apiUrl);
      const endTime = Date.now();
      
      console.log('📊 REPORTS API RESPONSE:', response.data);
      
      // ✅ INSTRUMENTATION: Log debug info for quick triage
      if (response.data.debug_info) {
        console.log('🔍 DEBUG INFO - Normalized UTC Window:', response.data.debug_info.normalized_utc_window);
        console.log('🔍 DEBUG INFO - MongoDB Query:', response.data.debug_info.mongodb_query);
        console.log('📈 DEBUG INFO - Result Counts:', response.data.debug_info.result_counts);
      }
      
      // Handle structured response from reports API
      const reportData = response.data;
      const ordersArray = reportData.orders || reportData || [];
      
      // ✅ LOG: Final results for validation
      console.log(`📊 FINAL RESULTS: Found ${ordersArray.length} orders`);
      if (ordersArray.length > 0) {
        console.log('📋 First few orders:', ordersArray.slice(0, 3));
      }
      
      // Format the response for the results display
      setReportResults({
        orders: ordersArray,
        total_count: reportData.summary?.total_orders || ordersArray.length || 0,
        carry_forward_count: reportData.summary?.carry_forward_count || 0,
        custom_report: reportData
      });
      
      setQueryTime(endTime - startTime);
      
      const totalOrders = response.data.summary?.total_orders || 0;
      const carryForward = response.data.summary?.carry_forward_count || 0;
      const cfMode = filters.includeCarryForward !== false;

      toast.success("Report generated successfully", {
        description: `Found ${totalOrders} orders${cfMode && carryForward > 0 ? ` (${carryForward} carried forward)` : ''}${!cfMode ? ' (strict date range)' : ''} in ${endTime - startTime}ms`
      });
      
    } catch (error) {
      console.error('Error running custom report:', error);
      setReportError('Failed to generate custom report. Please check your date range and try again.');
      toast.error("Failed to generate report", {
        description: "Please check your date range and try again or contact support if the problem persists."
      });
    } finally {
      setIsRunningReport(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Back Button and Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => setActiveView('orders')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-medium"
            data-testid="reporting-back-to-dashboard-btn"
          >
            <ChevronLeft className="h-5 w-5" />
            Back to Dashboard
          </Button>

          <div data-testid="reporting-header-title-block">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
              {isOrderDeskView ? 'Order Reports' : 'Advanced Reporting'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Generate and export operational reports
            </p>
          </div>
        </div>
        
        {/* Row Cap Warning */}
        {showRowCapWarning && exportPerformance?.row_cap_warning && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 w-full">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-orange-800 mb-1">Large Dataset Warning</h3>
                <p className="text-sm text-orange-700 mb-2">
                  {exportPerformance.row_cap_warning.warning_message}
                </p>
                <div className="text-xs text-orange-600">
                  <strong>Total rows:</strong> {exportPerformance.row_cap_warning.total_rows?.toLocaleString()} | 
                  <strong> Export limit:</strong> 50,000 rows
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center" data-testid="reporting-header-actions">
          {/* Consolidated Client Status Report Button (Excel with 2 sheets).
              REPORTS-CSV-REMOVE-001 (Sep 2026): header "Export CSV" removed globally. */}
          <Button 
            variant="outline" 
            onClick={handleConsolidatedReportExport} 
            disabled={loading || !filters.customStartDate || !filters.customEndDate || !filters.moduleScope}
            className="h-10 px-4 rounded-lg border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-colors flex items-center gap-2 font-medium text-sm disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200"
            title="Export Consolidated Client Status Report (Excel with color coding)"
            data-testid="reporting-client-status-report-btn"
          >
            Client Status Report
          </Button>
        </div>
      </div>

      {/* Role-Based Interface */}
      {(isOrderDeskView || user?.role === 'admin' || user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader' || user?.role === 'team_leader') ? (
        /* ===== REDESIGNED REPORTING - DATE RANGE BASED (Order Desk, Admin & Team Leaders) ===== */
        <div className="order-desk-reporting space-y-6">
          {/* Report Criteria Section - Using extracted component */}
          <ReportCriteriaCard
            filters={filters}
            setFilters={setFilters}
            isRunningReport={isRunningReport}
            onGenerateReport={runCustomReport}
            setReportResults={setReportResults}
            setReportError={setReportError}
          />

          {/* Results Table - Using extracted component */}
          <ReportResultsTable
            reportResults={reportResults}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            queryTime={queryTime}
            formatUtcToET={formatUtcToET}
            getTimestampTooltip={getTimestampTooltip}
            filters={filters}
          />

          {/* Loading State - Using extracted component */}
          {isRunningReport && <ReportLoadingState />}

          {/* Error Display - Using extracted component */}
          {reportError && <ReportErrorState error={reportError} onRetry={runCustomReport} />}
        </div>
      ) : (
        /* ===== ADVANCED TEAM LEADER INTERFACE ===== */
        <div className="space-y-6">
          {/* Row Cap Warning - Team Leader Only */}
          {showRowCapWarning && exportPerformance?.row_cap_warning && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-orange-800 mb-1">Large Dataset Warning</h3>
                  <p className="text-sm text-orange-700 mb-2">
                    {exportPerformance.row_cap_warning.warning_message}
                  </p>
                  <div className="text-xs text-orange-600">
                    <strong>Total rows:</strong> {exportPerformance.row_cap_warning.total_rows?.toLocaleString()} | 
                    <strong> Export limit:</strong> 50,000 rows
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Filters Card - Order Desk Only */}
          {isOrderDeskView && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>Filters</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={resetFilters}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    Reset Filters
              </Button>
              <Button 
                size="sm" 
                onClick={runReport} 
                disabled={isRunningReport}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isRunningReport ? 'Running Report...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Enhanced Date Range with Calendar */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Date Range</Label>
              <Select value={filters.dateRangePreset} onValueChange={(value) => updateFilters({ dateRangePreset: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                  <SelectItem value="last_30_days">Last 30 Days (Default)</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>

              {/* Enhanced Custom Date Range with Calendar */}
              {filters.dateRangePreset === 'custom' && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-600">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {filters.customStartDate ? formatUtcToET(filters.customStartDate, false).replace(' ET', '') : "Start date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.customStartDate ? new Date(filters.customStartDate) : undefined}
                          onSelect={(date) => handleCustomDateChange(date ? date.toISOString().split('T')[0] : '', filters.customEndDate)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-600">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {filters.customEndDate ? formatUtcToET(filters.customEndDate, false).replace(' ET', '') : "End date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.customEndDate ? new Date(filters.customEndDate) : undefined}
                          onSelect={(date) => handleCustomDateChange(filters.customStartDate, date ? date.toISOString().split('T')[0] : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}
            </div>

            {/* Other filters remain the same but simplified */}
            <AdvancedMultiSelect
              label="Status"
              options={filterOptions.statuses}
              value={filters.statuses}
              onChange={(value) => handleFilterChange('statuses', value)}
              includeAll={filters.includeAllStatuses}
              placeholder="All statuses"
            />

            <AdvancedMultiSelect
              label="Client Product"
              options={filterOptions.clientProducts}
              value={filters.clientProducts}
              onChange={(value) => handleFilterChange('clientProducts', value)}
              includeAll={filters.includeAllClientProducts}
              placeholder="All products"
            />

            <AdvancedMultiSelect
              label="Assigned To"
              options={filterOptions.assignedUsers}
              value={filters.assignedUsers}
              onChange={(value) => handleFilterChange('assignedUsers', value)}
              includeAll={filters.includeAllAssignedUsers}
              placeholder="All users"
            />

            <AdvancedMultiSelect
              label="Client Code"
              options={filterOptions.clientCodes}
              value={filters.clientCodes}
              onChange={(value) => handleFilterChange('clientCodes', value)}
              includeAll={filters.includeAllClientCodes}
              placeholder="All codes"
            />
          </div>
        </CardContent>
      </Card>
      )}
        </div>
      )}

      {/* Report Results Section */}
      {isRunningReport && (
        <Card className="shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-gray-600">Running report...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {reportError && (
        <Card className="border-red-200 bg-red-50 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-red-900">Report Error</p>
                  <p className="text-sm text-red-700">{reportError}</p>
                  <p className="text-xs text-red-600 mt-1">Try narrowing your filters if the data set is large.</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={runReport}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Results Table - HIDDEN (Duplicate - using Single Results Table above instead) */}
      {false && reportResults && (
        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Report Results</CardTitle>
                <p className="text-sm text-gray-600">
                  Total Orders: {reportResults.total_count || 0} | Query time: {queryTime}ms
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reportResults.orders && reportResults.orders.length > 0 ? (
              <div className="space-y-4">
                {/* Professional Results Table */}
                <div className="border rounded-lg overflow-hidden bg-white">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="tf-thead">
                        <TableRow>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Order Date</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Order #</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Product ID</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Customer</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Property Address</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">State</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">County</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Status</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Clarification Flag</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Client Code</TableHead>
                          <TableHead className="sticky top-0 bg-gray-50 font-semibold">Client Product</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportResults.orders.map((order) => {
                          // Clarification flag — mirrors backend derive_clarification_flag
                          // AND the dashboard's red ClarificationSentIndicator icon
                          // exactly. Revised May 6, 2026 (second follow-up):
                          // Yes iff the processor has marked clarification_sent=true
                          // AND the order is still in the 'clarification' status
                          // (i.e. response still pending). Once status flips to
                          // 'clarification_received', the dashboard icon hides AND
                          // this column shows No — they're the same concept.
                          const clarificationFlag = (
                            order.status === 'clarification' &&
                            order.clarification_sent === true
                          ) ? 'Yes' : 'No';
                          return (
                          <TableRow key={order.id} className="hover:bg-gray-50">
                            <TableCell 
                              className="text-sm cursor-help" 
                              title={getTimestampTooltip(order.created_at)}
                            >
                              {formatUtcToET(order.created_at, false)}
                            </TableCell>
                            <TableCell className="font-medium text-sm">{order.order_number}</TableCell>
                            <TableCell className="text-sm">{order.product_id || 'N/A'}</TableCell>
                            <TableCell className="text-sm">
                              {normalizeBorrowerName(order.borrower_name || order.customer_name) || 'N/A'}
                              {order.seller_name && <div className="text-[11px] text-slate-500">Seller: {normalizeBorrowerName(order.seller_name) || order.seller_name}</div>}
                            </TableCell>
                            <TableCell className="max-w-48 truncate text-sm" title={order.property_address}>
                              {order.property_address || 'N/A'}
                            </TableCell>
                            <TableCell className="text-sm">{order.property_state || 'N/A'}</TableCell>
                            <TableCell className="text-sm">{order.property_county || 'N/A'}</TableCell>
                            <TableCell>
                              <Badge variant={order.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                {order.status}
                              </Badge>
                            </TableCell>
                            <TableCell
                              className="text-sm"
                              data-testid={`clarification-flag-${order.order_number || order.id}`}
                            >
                              {clarificationFlag === 'Yes' ? (
                                <Badge variant="outline" className="border-orange-400 text-orange-700 bg-orange-50 text-xs font-semibold">
                                  Yes
                                </Badge>
                              ) : (
                                <span className="text-gray-500">No</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{order.client_code || 'N/A'}</TableCell>
                            <TableCell className="text-sm">{order.client_product || 'N/A'}</TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                
                {/* Enhanced Pagination - 50 rows per page */}
                {reportResults.total_count > 50 && (
                  <div className="flex items-center justify-between pt-4">
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, reportResults.total_count)} of {reportResults.total_count} results
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          runReport();
                        }}
                        disabled={currentPage === 1 || isRunningReport}
                      >
                        Previous
                      </Button>
                      <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                        Page {currentPage} of {Math.ceil(reportResults.total_count / 50)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCurrentPage(prev => prev + 1);
                          runReport();
                        }}
                        disabled={currentPage >= Math.ceil(reportResults.total_count / 50) || isRunningReport}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <Search className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Results Found</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    No orders match your current filter criteria. Try adjusting your filters or selecting a different date range.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={resetFilters}
                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <>
          {/* Export Performance Info */}
          {analytics.export_performance && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900">Export Information</p>
                    <p className="text-xs text-blue-700">
                      {analytics.filter_summary?.total_matching_records || 0} records match filters
                    </p>
                    <p className="text-xs text-blue-700">
                      Recommended: {analytics.export_performance.recommended_format?.toUpperCase()}
                      {analytics.export_performance.will_use_streaming && " (Streaming enabled)"}
                    </p>
                  </div>
                  <div className="text-right text-blue-800">
                    <p className="text-lg font-bold">~{analytics.export_performance.estimated_size_mb}MB</p>
                    <p className="text-xs">Est. size</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Analytics boxes removed - Clean interface for Order Desk */}

          {/* Analytics dashboard boxes removed - Clean streamlined interface for Order Desk */}
        </>
      )}

      {loading && (
        <div className="text-center p-8">
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      )}
    </div>
  );
};

export default ReportingDashboard;
